import React, { useMemo, useState } from 'react';

import {
  U21Button,
  U21Chip,
  U21Spacer,
  U21Subsection,
  U21TitleCountLabel,
  U21Divider,
  U21MenuLayout,
  U21NoValue,
} from 'app/shared/u21-ui/components';
import { IconArrowRight, IconEye, IconEyeOff } from '@u21/tabler-icons';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import {
  selectActionEventFingerprintSettings,
  selectDataSettingsByNativeKey,
  selectTransactionFingerprintSettings,
} from 'app/modules/dataSettings/selectors';
import {
  FullTxnResponse,
  ShortTxnResponse,
} from 'app/modules/transactions/types';
import {
  CachedActionEventResponse,
  ShortActionEventResponse,
} from 'app/modules/actionEvents/responses';
import { mapFieldsForSidebarSection } from 'app/modules/sidebar/components/utils/SidebarSummary';
import { DeviceIntelligenceChip } from 'app/modules/deviceIntelligence/components/DeviceIntelligenceChip';
import {
  useFingerprintCount,
  useFingerprintNewlySeen,
  useHasFingerprintData,
} from 'app/modules/deviceIntelligence/hooks/fingerprint';
import {
  FormattedData,
  Unit21DataClassifier,
  TxnEventNativeFields,
  ActionEventNativeFields,
} from 'app/modules/dataSettings/responses';

type FingerprintVisitorId = {
  external_id: string;
  unit21_id: string;
  flow: 'INBOUND' | 'OUTBOUND' | 'OTHER';
  formatted_data: FormattedData;
};

type DeviceIntelligenceChipPopOverProps = {
  event: 'txn' | 'action';
  row:
    | FullTxnResponse
    | CachedActionEventResponse
    | ShortActionEventResponse
    | ShortTxnResponse;
};

export const DeviceIntelligenceChipPopOver = ({
  event,
  row,
}: DeviceIntelligenceChipPopOverProps) => {
  const [showUndetectedFields, setShowUndetectedFields] = useState(false);
  const [open, setOpen] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const handleClose = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    setOpen(false);
    setShowUndetectedFields(false);
  };

  const fingerprintSettings = useSelector(
    event === 'txn'
      ? selectTransactionFingerprintSettings
      : selectActionEventFingerprintSettings,
  );

  const trueCount = useFingerprintCount(event, row);
  const isNewlySeen = useFingerprintNewlySeen(event, row);

  const dataSettingsByNativeKey = useSelector(selectDataSettingsByNativeKey);

  const fingerprintVisitorId = useMemo<FingerprintVisitorId | null>(() => {
    const fingerprintVisitorIdSetting =
      event === 'txn'
        ? dataSettingsByNativeKey[Unit21DataClassifier.EVENT][
            TxnEventNativeFields.FINGERPRINT_VISITOR_ID
          ]
        : dataSettingsByNativeKey[Unit21DataClassifier.ACTION_EVENT][
            ActionEventNativeFields.FINGERPRINT_VISITOR_ID
          ];

    if (fingerprintVisitorIdSetting) {
      const formattedData = row.formatted_data[fingerprintVisitorIdSetting.id];
      const formattedValue = formattedData?.formatted_value;

      if (
        formattedValue &&
        typeof formattedValue === 'object' &&
        'unit21_id' in formattedValue &&
        typeof formattedValue.unit21_id === 'string'
      ) {
        return formattedValue;
      }
    }
    return null;
  }, [dataSettingsByNativeKey, row.formatted_data, event]);

  const hasFingerprintData = useHasFingerprintData(event, row);

  if (!hasFingerprintData) {
    return <U21NoValue />;
  }

  return (
    <U21MenuLayout
      trigger={
        <DeviceIntelligenceChip event={event} row={row} onClick={handleClick} />
      }
      open={open}
      onClose={handleClose}
      alignRight
      onClick={(e) => e.stopPropagation()}
    >
      <U21Subsection
        title={
          <U21TitleCountLabel
            count={hasFingerprintData && trueCount > 0 ? trueCount : null}
            label=""
            color={trueCount > 0 ? 'error' : 'default'}
          >
            Device Signals
          </U21TitleCountLabel>
        }
        action={
          isNewlySeen ? (
            <U21Chip color="warning">Newly seen</U21Chip>
          ) : undefined
        }
      >
        <U21Divider horizontal />
        {hasFingerprintData && (
          <StyledScrollableContainer>
            {fingerprintSettings.map((f) =>
              mapFieldsForSidebarSection(f, row, showUndetectedFields),
            )}
          </StyledScrollableContainer>
        )}
        <U21Divider horizontal />
        <StyledFooterSpacer horizontal>
          <U21Button
            color="inherit"
            size="small"
            startIcon={showUndetectedFields ? <IconEyeOff /> : <IconEye />}
            onClick={(e) => {
              e.stopPropagation();
              setShowUndetectedFields(!showUndetectedFields);
            }}
          >
            {showUndetectedFields
              ? 'Hide Undetected Fields'
              : 'Show Undetected Fields'}
          </U21Button>
          {fingerprintVisitorId?.unit21_id && (
            <U21Button
              aria-label="Go to device details"
              color="inherit"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
              }}
              to={`/instruments/${fingerprintVisitorId.unit21_id}`}
            >
              <IconArrowRight />
            </U21Button>
          )}
        </StyledFooterSpacer>
      </U21Subsection>
    </U21MenuLayout>
  );
};

const StyledFooterSpacer = styled(U21Spacer)`
  padding-top: 16px;
  justify-content: space-between;
`;

const StyledScrollableContainer = styled(U21Spacer)`
  flex: 1;
  overflow-y: auto;
  max-height: 300px;
  padding-top: 8px;
  padding-bottom: 8px;
`;
